<template>
  <div class="container">
    <Loader v-if="isLoading" />
    <div class="p-booking__inner-wrapper">
      <div class="p-booking__title pt-4">{{ dictionary.selectTickets }}</div>
      <Alert
        v-if="!isLoading && store.errorMsgs.length"
        type="danger"
        dismiss-by-native=""
        :messages="store.errorMsgs"
        @close="removeErrors" />

      <div class="p-booking__tickets-wrapper">
        <div class="p-booking__tickets-choice">
          <Tickets class-name="mt-3" @fetch-tickets="fetchTickets" />
        </div>
        <div class="p-booking__tickets-total">
          <CartSummary class="mt-3" />

          <CartPoints></CartPoints>

          <div>
            <div
              :class="collapseOpened"
              class="b-ticket-summary__expand"
              @click="openExpand">
              <div
                class="b-ticket-summary__expand__header"
                :class="iconCollapseOpened">
                <div class="b-ticket-summary__expand__title">
                  {{ dictionary.addVoucher }}
                </div>
              </div>
              <div class="b-ticket-summary__expand__content">
                <div class="b-ticket-summary__expand__text">
                  {{ dictionary.voucherTabDesc }}
                </div>
                <button
                  type="button"
                  class="b-ticket-summary__expand__cta btn-primary-orange"
                  @click="openVoucherModel">
                  <span>{{ dictionary.enterVoucherCode }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ModalOverlay
      v-model="showVoucherModal"
      :header-background-class="
        desktop ? 'b-pop-up__header' : 'b-pop-up__title-holder'
      "
      background-class="b-pop-up--alert-success b-pop-up__holder"
      no-close-backdrop
      @close="onCloseModal">
      <template #header>
        <div v-if="desktop" class="b-pop-up__header__title">
          {{ voucherTitle }}
        </div>
        <i v-if="!desktop" class="icon-ticket mr-1"></i>
        <div v-if="!desktop" class="ml-2 b-pop-up__header__title">
          {{ voucherTitle }}
        </div>
      </template>
      <template #body>
        <AddVoucherModal
          v-if="showAddVoucher"
          :allow-discount-vouchers="
            store.getVoucherType !== constants.prepaidVoucherType
          "
          :voucher-title="voucherTitle"
          :input-title="inputTitle"
          :validation-error="dictionary.voucherRequired"
          :voucher-code-validation-error="dictionary.voucherCodeInvalid"
          :incompatible-coupon-types-error="dictionary.incompatibleCouponTypes"
          @add-vouchers="confirmVouchers" />
        <VoucherConfirmModal
          v-if="showOldVoucherReset"
          @confirm-vouchers="confirmVouchers"
          @back-to-add-vouchers="backToAddVouchers" />
      </template>
    </ModalOverlay>
    <ModalOverlay
      v-model="showFamilyClubModal"
      background-class="b-pop-up--alert-success b-pop-up__holder"
      :header-background-class="
        desktop ? 'b-pop-up__header' : 'b-pop-up__title-holder'
      ">
      <template #header>
        <div v-if="desktop" class="b-pop-up__header__title">
          Village Family Pass
        </div>
        <i v-if="!desktop" class="icon-village"></i>
        <div v-if="!desktop" class="b-pop-up__header__title">
          Village Family Pass
        </div>
      </template>
      <template #body>
        <div class="p-booking__modal__family-club__content">
          <div class="p-booking__modal__family-club__club-img">
            <img
              src="/static/cards/Membership-cards-horizontal.png"
              alt="Village Family Pass" />
          </div>
          <div class="p-booking__modal__family-club">
            <div
              class="p-booking__modal__family-club__title"
              v-html="familyClubModalDict.title"></div>
            <div
              class="p-booking__modal__family-club__desc"
              v-html="familyClubModalDict.text"></div>
            <div class="p-booking__modal__family-club__personal">
              <div
                class="p-booking__modal__family-club__personal__title"
                v-html="familyClubModalDict.loggedInTitle"></div>
              <div
                class="p-booking__modal__family-club__personal__desc"
                v-html="familyClubModalDict.loggedInText"></div>
            </div>
            <div class="p-booking__modal__family-club__cta-area">
              <Link
                class="btn-primary-orange"
                :url="familyClubCta.url"
                :target="familyClubCta.target"
                :title="familyClubCta.title"
                :include-span="true" />
              <button
                type="button"
                class="btn-secondary btn--block"
                @click="() => (showFamilyClubModal = false)">
                <span>{{ dictionary.continueAsGuest }}</span>
              </button>
            </div>
          </div>
        </div>
      </template>
    </ModalOverlay>
  </div>
</template>

<script setup>
import axios from 'axios'
import CartSummary from '../../components/Booking/Shared/CartSummary.vue'
import { computed, getCurrentInstance, nextTick, onMounted, ref } from 'vue'
import { useBookingStore } from '../../store/BookingStore'
import ModalOverlay from '../../components/ModalOverlay.vue'
import AddVoucherModal from '../../components/Booking/Step2/AddVoucherModal.vue'
import VoucherConfirmModal from '../../components/Booking/Step2/VoucherConfirmModal.vue'
import Tickets from '../../components/Booking/Step2/Tickets.vue'
import Link from '../../components/UI/Link.vue'
import useViewport from '../../../features/viewport'
import constants from '../../../lib/constants'
import Loader from '../../components/Shared/Loader.vue'
import Alert from '../../components/Shared/Alert.vue'
import CartPoints from '../../components/Booking/Shared/CartPoints.vue'
import postRequestHandler from './../../../lib/postRequestHandler'
import getErrorMessage from './../../../lib/getErrorMessage'
import { scrollToError } from './../../../lib/lib'
import * as lib from './../../../lib/lib'

const { desktop } = useViewport()
const store = useBookingStore()

const showVoucherModal = ref(false)
const isLoading = ref(false)

const { dictionary, configuration } =
  getCurrentInstance().appContext.config.globalProperties
const { ctaRegisterFamilyClub, ctaRegisterNewAccount } = configuration

const showAddVoucher = ref(true)
const voucherTitle = ref('')
const showOldVoucherReset = ref(false)

const showFamilyClubModal = ref(false)
const familyClubModalDict = ref(null)
const updateFamilyClubModalDict = (data) => {
  if (data) {
    familyClubModalDict.value = { ...data }
  } else {
    familyClubModalDict.value = null
  }
}
const inputTitle = ref(dictionary.codesNumbersVoucher)

const openCollapse = ref(false)

const collapseOpened = computed(() => {
  return openCollapse.value ? 'b-ticket-summary__expand__open' : ''
})

const iconCollapseOpened = computed(() => {
  return openCollapse.value
    ? 'b-ticket-summary__expand__header__icon--open'
    : ''
})

const familyClubCta = computed(() => {
  // means user already have account but not a memeber in family club
  if (familyMember.value) {
    return ctaRegisterFamilyClub
  } else if (generalMember.value) {
    return ctaRegisterNewAccount
  }
  return ctaRegisterFamilyClub
})

const familyMember = computed(() => {
  return (
    !store.bookingProgress.paymentDetails.familyClubMember &&
    store.bookingProgress.paymentDetails.email
  )
})

const generalMember = computed(() => {
  return (
    store.bookingProgress.paymentDetails.familyClubMember &&
    !store.bookingProgress.paymentDetails.email
  )
})

const openExpand = () => {
  openCollapse.value = !openCollapse.value
}

const triggerOnlyMembersLogin = () => {
  const { selectedMovie } = store.bookingProgress

  jQuery(document).trigger('login-clicked', {
    action: 'onlyMembersLogin',
    onlyMembersInfo: {
      date: selectedMovie.onlyMembersAvailableDate,
      image: {
        url: selectedMovie.img,
        alt: selectedMovie.title,
      },
    },
  })
}

onMounted(async () => {
  // check if user is in family club & movie of for kids
  await checkLogin()

  await checkFamilyTypeMovie()
  store.resetAllTicketsVouchers()
  fetchTickets()

  document.addEventListener('login-was-held', () => {
    store.resetAllTicketsVouchers()
    fetchTickets()
  })
})

const checkLogin = () => {
  return new Promise((resolve, reject) => {
    removeErrors()
    isLoading.value = true

    const postData = {}

    postRequestHandler({
      postData,
      endpoint: configuration.endpointCheckLogin,

      successCallback: (response) => {
        checkLoginHandleSuccess(response)
        resolve()
      },

      errorCallback: (messages) => {
        handleError(messages)
        reject()
      },
      captchaAction: configuration.checkLogincaptchaAction,
      refreshTokenHandler: configuration.refresh,
    })
  })
}

const checkFamilyTypeMovie = async () => {
  return new Promise((resolve, reject) => {
    removeErrors()
    isLoading.value = true

    const postData = {
      movie: store.bookingProgress.selectedMovie.id,
      cinema: store.bookingProgress.selectedCinema.value,
      language: document.documentElement.lang,
    }

    postRequestHandler({
      postData,
      endpoint: configuration.familyMovies,

      successCallback: (response) => {
        checkFamilyMovieSuccess(response)
        resolve()
      },
      errorCallback: (messages) => {
        handleError(messages)
        reject()
      },
      captchaAction: configuration.familyMoviescaptchaAction,
      refreshTokenHandler: configuration.refresh,
    })
  })
}

const checkFamilyMovieSuccess = (response) => {
  const { action } = response.data

  if (action === 'popupWithLogin') {
    jQuery(document).trigger('login-clicked', {
      ...response.data,
    })
  } else if (action === 'popupWithoutLogin') {
    updateFamilyClubModalDict(response.data)
    showFamilyClubModal.value = true
  } else if (action === 'popupOnlyLogin') {
    if (store.bookingProgress.selectedMovie.onlyMembers) {
      triggerOnlyMembersLogin()
    } else {
      jQuery(document).trigger('login-clicked')
    }
  }
}

const checkLoginHandleSuccess = (response) => {
  isLoading.value = false
  removeErrors()

  const { accountData } = response.data

  if (!accountData) {
    return false
  }

  store.setAccountData(accountData)
  store.setPaymentDetails(accountData)
}

const removeErrors = () => {
  store.assignGlobalErrorMessages([])
}

const handleError = async (messages) => {
  isLoading.value = false

  store.assignGlobalErrorMessages(
    getErrorMessage(messages, dictionary.defaultErrorMessage)
  )

  await nextTick()
  scrollToError()
}

const openVoucherModel = () => {
  voucherTitle.value = dictionary.addVoucher
  showAddVoucher.value = true
  showOldVoucherReset.value = false
  showVoucherModal.value = true
  inputTitle.value = dictionary.codesNumbersVoucher
}

const validateAllVoucherCodes = async () => {
  const postData = {
    cinemaId: store.bookingProgress.selectedCinema?.value,
    sessionId: store.bookingProgress.selectedRoom?.id,
    codes: store.userVouchersCodesModal.map((i) => i.code),
    userSessionId: store.bookingProgress.usersessionId,
    language: document.documentElement.lang,
  }

  await postRequestHandler({
    postData,
    endpoint: configuration.endpointTicketsVoucher,
    successCallback: (resp) => {
      if (resp.data.success) {
        store.assignDiscountVoucherCodes(
          resp.data.validationResults,
          dictionary.voucherInOrder
        )
        if (store.getUserVouchersCodesModal.every((i) => i.valid)) {
          showVoucherModal.value = false
        }
      }
    },
    errorCallback: (e) => {
      handleError(e)
      showVoucherModal.value = false
    },
    captchaAction: configuration.ticketsVoucherCaptchaAction,
    refreshTokenHandler: configuration.refresh,
  })
}

// const validateDiscountVoucherCodes = async () => {
//   const postData = {
//     cinemaId: store.bookingProgress.selectedCinema?.value,
//     sessionId: store.bookingProgress.selectedRoom?.id,
//     codes: store.userVouchersCodesModal.map((i) => i.code),
//     userSessionId: store.bookingProgress.usersessionId,
//   }

//   await postRequestHandler({
//     postData,
//     endpoint: configuration.endpointTicketsVoucher,
//     successCallback: (resp) => {
//       if (resp.data.success) {
//         store.assignDiscountVoucherCodes(
//           resp.data.validationResults,
//           dictionary.voucherInOrder
//         )
//         if (store.getUserVouchersCodesModal.every((i) => i.valid)) {
//           showVoucherModal.value = false
//         }
//       }
//     },
//     errorCallback: (e) => {
//       handleError(e)
//       showVoucherModal.value = false
//     },
//     captchaAction: configuration.ticketsVoucherCaptchaAction,
//     refreshTokenHandler: configuration.refresh,
//   })
// }

// const validatePrepaidVoucherCodes = async () => {
//   const postData = {
//     cinemaId: store.bookingProgress.selectedCinema?.value,
//     sessionId: store.bookingProgress.selectedRoom?.id,
//     codes: store.userVouchersCodesModal.map((i) => i.code),
//     userSessionId: store.bookingProgress.usersessionId,
//   }

//   await postRequestHandler({
//     postData,
//     endpoint: configuration.endpointTicketsVoucher,
//     successCallback: (resp) => {
//       if (resp.data.success) {
//         store.assignPrepaidVoucherCodes(
//           resp.data.validationResults,
//           dictionary.voucherInOrder
//         )

//         if (store.getUserVouchersCodesModal.every((i) => i.valid)) {
//           showVoucherModal.value = false
//         } else {
//           backToAddVouchers()
//         }
//       } else {
//         backToAddVouchers()
//       }
//     },
//     errorCallback: (e) => {
//       handleError(e)
//       showVoucherModal.value = false
//     },
//     captchaAction: configuration.ticketsVoucherCaptchaAction,
//     refreshTokenHandler: configuration.refresh,
//   })
// }

const validateVoucherCodes = () => {
  // const prepaidLength = constants.vouchers.prepaid.length

  // if ( store.getUserVouchersCodesModal.every((i) => i.code.trim().length === prepaidLength )
  // ) {
  //   return validatePrepaidVoucherCodes()
  // }

  // if (store.getVoucherType === constants.prepaidVoucherType) {
  //   throw new Error('Invalid voucher codes')
  // }

  return validateAllVoucherCodes()
}

const confirmVouchers = async () => {
  store.assignGlobalErrorMessages([])
  isLoading.value = true
  try {
    await validateVoucherCodes()
  } catch (error) {
    showVoucherModal.value = false
    console.error(error)
    store.assignGlobalErrorMessages([error])
  }
  isLoading.value = false
}

// const showConfirmModal = () => {
//   showAddVoucher.value = false
//   showOldVoucherReset.value = true
// }

const backToAddVouchers = () => {
  showAddVoucher.value = true
  showOldVoucherReset.value = false
}

const fetchTickets = async () => {
  isLoading.value = true

  const { selectedMovie, selectedCinema, selectedRoom } = store.bookingProgress

  const postData = {
    movieId: selectedMovie?.movieId,
    cinemaId: selectedCinema?.value,
    room: selectedRoom?.id,
  }

  const endpoint = configuration['movie-tickets']
  try {
    const resp = await axios.post(endpoint, postData, lib.axiosConfig)

    const { data } = resp

    if (data.hasError) {
      return handleError(data.errorMessages)
    }

    const { ticketTypes, vouchers, concessionsAllowed, userSessionId } =
      data.data

    store.setUsersessionId(userSessionId)
    store.setTickets(ticketTypes, vouchers, concessionsAllowed)

    isLoading.value = false
  } catch (errors) {
    handleError([dictionary.defaultErrorMessage])
    console.error(errors)
  }
}

const onCloseModal = () => {
  store.resetUserVouchersCodesModal()
}
</script>

<style lang="scss" scoped>
.loader {
  position: fixed;
}
</style>
